
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { handleSignUp, handleSignIn } from "./auth-utils";
import { getAndClearDeepLink } from "@/utils/deepLinking";

export const useAuthForm = (
  externalFormType?: 'login' | 'signup',
  externalToggleFormType?: () => void
) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("quant");
  const [isSignUp, setIsSignUp] = useState(externalFormType === 'signup');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent, reCaptchaToken: string) => {
    e.preventDefault();
    try {
      if (!email || !password) {
        throw new Error("Please fill in both email and password");
      }

      setIsLoading(true);
      setError(null);

      // Accept fallback token in development/testing
      if (reCaptchaToken === "fallback_token") {
        console.warn("Proceeding with fallback verification");
      }

      const result = isSignUp 
        ? await handleSignUp(email, password, userType)
        : await handleSignIn(email, password);

      if (result.success) {
        toast({
          title: isSignUp ? "Account created!" : "Welcome back!",
          description: isSignUp 
            ? "Your account has been created successfully."
            : "You have successfully signed in.",
        });
        
        // Check for stored deep link and redirect appropriately
        const deepLink = getAndClearDeepLink();
        window.location.href = deepLink || '/dashboard';
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFormType = () => {
    if (externalToggleFormType) {
      externalToggleFormType();
    } else {
      setIsSignUp(!isSignUp);
    }
    setError(null);
  };

  const handleForgotPassword = () => {
    toast({
      title: "Password Reset",
      description: "Password reset functionality is coming soon!",
    });
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    userType,
    setUserType,
    isSignUp,
    setIsSignUp,
    isLoading,
    setIsLoading,
    error,
    setError,
    handleSubmit,
    handleToggleFormType,
    handleForgotPassword,
  };
};
